import { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  AlloyButton,
  ComponentLoader,
  ModalContext,
  RadioGroup,
  Radio
} from 'cat-ecommerce-alloy';
import useSystemFeedback from '@app/hooks/useSystemFeedback';
import ExceptionFeedbackBySelector from '../../Exception/ExceptionFeedbackBySelector';
import { ERROR_DOMAIN } from '@app/constants/errorConstants';
import AVIAddressLabel from '../AVIAddressLabel';

const AVIModal = ({
  addressDomain,
  current,
  locale,
  onCancel,
  onSubmit,
  required = false,
  suggested,
  title
}) => {
  const [t] = useTranslation();
  const modalCtx = useContext(ModalContext);
  const { clearError } = useSystemFeedback();
  const {
    address1: currentAddress1,
    address2: currentAddress2,
    city: currentCity,
    state: currentState,
    zipCode: currentZipCode,
    country: currentCountry
  } = current || {};

  const {
    address1: suggestedAddress1,
    address2: suggestedAddress2,
    city: suggestedCity,
    state: suggestedState,
    zipCode: suggestedZipCode,
    country: suggestedCountry
  } = suggested || {};
  const currentStr = 'original';
  const suggestedStr = 'suggested';

  const isLoading = useSelector(state => state[addressDomain]?.aviLoading);

  const [selected, setSelected] = useState(
    suggested ? suggestedStr : currentStr
  );
  const errorDomain =
    addressDomain === 'checkout'
      ? ERROR_DOMAIN.CHECKOUT
      : ERROR_DOMAIN.ADDRESS_BOOK;
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    clearError(errorDomain);
    modalCtx.removeCurrentModal();
  };
  const handleSubmit = () => {
    clearError(errorDomain);
    return onSubmit(selected === suggestedStr, selected);
  };

  return (
    <>
      <h2 className="h2 display-1">{title}</h2>
      <ExceptionFeedbackBySelector
        selector={state => state?.errors[addressDomain]?.modal}
      />
      {isLoading && <ComponentLoader />}
      <p className="exclude-global mt-3">{t('AVI_ADDRESS_NOT_FOUND')}</p>
      {current && !required ? ( //when dealer has AVI enabled but not required
        <RadioGroup selected={selected} setSelected={setSelected}>
          <Radio
            name="addresses"
            id="suggestedAddress"
            className="border-top py-3"
            label={
              <AVIAddressLabel
                address1={suggestedAddress1}
                address2={suggestedAddress2}
                city={suggestedCity}
                country={suggestedCountry}
                locale={locale}
                state={suggestedState}
                title={t('ES_RECOMMENDATIONS')}
                zipCode={suggestedZipCode}
              />
            }
            value={suggestedStr}
          />
          <Radio
            name="addresses"
            id="currentAddress"
            className="border-top border-bottom py-3"
            label={
              <AVIAddressLabel
                address1={currentAddress1}
                address2={currentAddress2}
                city={currentCity}
                country={currentCountry}
                locale={locale}
                state={currentState}
                title={t('AVI_ORIGINAL_ADDRESS')}
                zipCode={currentZipCode}
              />
            }
            value={currentStr}
          />
        </RadioGroup>
      ) : (
        //when dealer has AVI required you must use the recommended address, so no options here
        <div className="pb-2">
          <AVIAddressLabel
            address1={suggestedAddress1}
            address2={suggestedAddress2}
            city={suggestedCity}
            country={suggestedCountry}
            locale={locale}
            state={suggestedState}
            title={t('ES_RECOMMENDATIONS')}
            zipCode={suggestedZipCode}
          />
        </div>
      )}
      <div className="d-flex flex-column flex-md-row">
        <AlloyButton
          buttonType="secondary"
          size="dynamic"
          onClick={handleSubmit}
          className="me-md-3"
        >
          {t('EF_CONTINUE')}
        </AlloyButton>
        <AlloyButton buttonType="ghost" size="dynamic" onClick={handleCancel}>
          {t('CANCEL')}
        </AlloyButton>
      </div>
    </>
  );
};

AVIModal.propTypes = {
  addressDomain: PropTypes.string,
  current: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    country: PropTypes.string
  }),
  locale: PropTypes.string,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  required: PropTypes.bool,
  suggested: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    country: PropTypes.string
  }),
  title: PropTypes.string
};

export default AVIModal;
